import React, { useState } from "react";
import { userApi, cookiesApi } from "../../api/auctiongate.js";

/**
 * "Admin" / maintenance tools bundled together:
 *   - Update auction balance
 *   - Send new title (doc type)
 *   - Upload PDF payment
 *   - Clear cookies (per-auction, informational — web apps can't do cross-domain cookies)
 */
const AUCTIONS = ["copart", "copart_canada", "iaai", "iaai_canada", "manheim", "happycarservice", "windmotors"];

export default function AdminTools() {
  return (
    <div className="card">
      <div className="card__title">Admin / maintenance</div>
      <div className="grid grid-2">
        <UpdateBalance />
        <SendNewTitle />
        <PdfPayment />
        <ClearCookies />
      </div>
    </div>
  );
}

function UpdateBalance() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const onSubmit = async (e) => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.currentTarget).entries());
    setBusy(true);
    setMsg(null);
    try {
      const r = await userApi.updateBalance({ auction_id: Number(f.auction_id), amount: Number(f.amount) });
      setMsg(r?.message || "Done.");
    } catch (err) {
      setMsg(err?.message || "Failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={onSubmit} className="stack" style={box}>
      <b style={sub}>Update auction balance</b>
      <select name="auction_id" defaultValue="2" style={input}>
        <option value="1">IAAI (1)</option>
        <option value="2">Copart (2)</option>
        <option value="11">IAAI Canada (11)</option>
        <option value="14">Copart Canada (14)</option>
      </select>
      <input name="amount" type="number" placeholder="New balance (USD)" required style={input} />
      <button className="btn btn-primary" disabled={busy}>
        {busy ? <span className="loader" /> : "Update"}
      </button>
      {msg && <div className="small muted">{String(msg)}</div>}
    </form>
  );
}

function SendNewTitle() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const onSubmit = async (e) => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.currentTarget).entries());
    setBusy(true);
    setMsg(null);
    try {
      const r = await userApi.newTitle({ auctionId: f.auction_id, state: f.state, title: f.title });
      setMsg(r?.message || "Submitted.");
    } catch (err) {
      setMsg(err?.message || "Failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={onSubmit} className="stack" style={box}>
      <b style={sub}>Send new title (doc type)</b>
      <select name="auction_id" defaultValue="2" style={input}>
        <option value="1">IAAI (1)</option>
        <option value="2">Copart (2)</option>
        <option value="11">IAAI Canada (11)</option>
        <option value="14">Copart Canada (14)</option>
      </select>
      <input name="state" placeholder="State (e.g. TX)" required maxLength={4} style={input} />
      <input name="title" placeholder='Title type (e.g. "CERTIFICATE OF TITLE")' required style={input} />
      <button className="btn btn-primary" disabled={busy}>
        {busy ? <span className="loader" /> : "Send"}
      </button>
      {msg && <div className="small muted">{String(msg)}</div>}
    </form>
  );
}

function PdfPayment() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const onSubmit = async (e) => {
    e.preventDefault();
    const f = e.currentTarget;
    const fd = new FormData(f);
    const file = fd.get("file");
    if (!(file instanceof File) || !file.size) {
      setMsg("Attach a PDF first.");
      return;
    }
    setBusy(true);
    setMsg(null);
    // Read file as base64 so we can send JSON through our proxy.
    const b64 = await fileToBase64(file);
    try {
      const r = await userApi.pdfPayment({
        lot: {
          lot_number: fd.get("lot_number"),
          auction_id: fd.get("auction_id"),
          vin: fd.get("vin") ?? "",
        },
        file: { content: b64, filename: file.name, mime: file.type },
      });
      setMsg(r?.data?.message || "Uploaded (proxy forwarded).");
    } catch (err) {
      setMsg(err?.message || "Failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={onSubmit} className="stack" style={box}>
      <b style={sub}>Upload PDF payment</b>
      <select name="auction_id" defaultValue="2" style={input}>
        <option value="1">IAAI (1)</option>
        <option value="2">Copart (2)</option>
      </select>
      <input name="lot_number" placeholder="Lot number" required style={input} />
      <input name="vin" placeholder="VIN (optional)" style={input} />
      <input name="file" type="file" accept="application/pdf" required style={{ fontSize: 12 }} />
      <button className="btn btn-primary" disabled={busy}>
        {busy ? <span className="loader" /> : "Upload"}
      </button>
      {msg && <div className="small muted">{String(msg)}</div>}
    </form>
  );
}

function ClearCookies() {
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const clear = async (a) => {
    setBusy(a);
    setMsg(null);
    try {
      const r = await cookiesApi.clear(a);
      setMsg(r?.note || "OK.");
    } catch (err) {
      setMsg(err?.message || "Failed.");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="stack" style={box}>
      <b style={sub}>Clear auction cookies</b>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {AUCTIONS.map((a) => (
          <button key={a} className="btn btn-ghost" disabled={busy === a} onClick={() => clear(a)} style={{ fontSize: 11, padding: "5px 10px" }}>
            {busy === a ? <span className="loader" /> : a}
          </button>
        ))}
      </div>
      {msg && <div className="small muted" style={{ maxWidth: 320 }}>{String(msg)}</div>}
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || "").split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const box = {
  padding: 12,
  border: "1px solid var(--color-border)",
  borderRadius: 10,
  background: "var(--color-bg-card-elevated)",
};
const sub = { fontSize: 13, color: "#0f172a", marginBottom: 4 };
const input = {
  padding: "7px 10px",
  border: "1px solid var(--color-border-input)",
  borderRadius: 8,
  fontSize: 13,
  background: "#fff",
};
